import { api } from '@/lib/api';
import type {
  IApiResponse,
  IPortfolioSummaryResponse,
  IProductTimeSeriesResponse,
  IProductP85Response,
  IPortfolioEpicCompletionResponse,
  IEpicBreakdownResponse,
  IPortfolioDoraTrendResponse,
  IDevExHistoryResponse,
  IPortfolioQueryParams,
  IWorkflowHygieneResponse,
  IUnlinkedItemsDrillResponse,
  INonSpineStatusesDrillResponse,
  INonCanonicalTypesDrillResponse,
  IWipTrendPoint,
  ICurrentWipCountResponse,
  IWipAvgAge,
} from '@skystone/shared';

// PRODOPSAI-415: scope filter for canonical WIP / Open queries. All fields
// optional — `asOfDate` is computed inside the backend repository.
export interface FlowMetricsQueryParams {
  from?: string;
  to?: string;
  region?: string;
  productId?: string;
  teamId?: string;
}

interface DevExHistoryParams {
  region?: string;
  productId?: string;
  teamId?: string;
}

function buildQuery(params: IPortfolioQueryParams & { page?: number; pageSize?: number }): string {
  const search = new URLSearchParams();
  search.set('from', params.from);
  search.set('to', params.to);
  if (params.granularity) search.set('granularity', params.granularity);
  if (params.region) search.set('region', params.region);
  if (params.productId) search.set('productId', params.productId);
  if (params.teamId) search.set('teamId', params.teamId);
  if (params.groupBy) search.set('groupBy', params.groupBy);
  // PRODOPSAI-621: opt-in inclusion of archived teams' issues (PRODOPSAI-619
  // backend param). Omitted entirely when not opted in.
  if (params.includeArchived === true) search.set('includeArchived', 'true');
  // Workflow Hygiene drill-down endpoints (PRODOPSAI-385) extend the base
  // query with server-side pagination. Other endpoints simply ignore these.
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  return `?${search.toString()}`;
}

export const portfolioApi = {
  getSummary: async (params: IPortfolioQueryParams): Promise<IPortfolioSummaryResponse> => {
    const result = await api.get<IApiResponse<IPortfolioSummaryResponse>>(
      `/portfolio/summary${buildQuery(params)}`,
    );
    return result.data;
  },

  getThroughputTrend: async (params: IPortfolioQueryParams): Promise<IProductTimeSeriesResponse> => {
    const result = await api.get<IApiResponse<IProductTimeSeriesResponse>>(
      `/portfolio/throughput-trend${buildQuery(params)}`,
    );
    return result.data;
  },

  getCycleTimeP85ByProduct: async (params: IPortfolioQueryParams): Promise<IProductP85Response> => {
    const result = await api.get<IApiResponse<IProductP85Response>>(
      `/portfolio/cycle-time-p85-by-product${buildQuery(params)}`,
    );
    return result.data;
  },

  getEpicCompletion: async (params: IPortfolioQueryParams): Promise<IPortfolioEpicCompletionResponse> => {
    const result = await api.get<IApiResponse<IPortfolioEpicCompletionResponse>>(
      `/portfolio/epic-completion${buildQuery(params)}`,
    );
    return result.data;
  },

  getWorkflowHygiene: async (params: IPortfolioQueryParams): Promise<IWorkflowHygieneResponse> => {
    const result = await api.get<IApiResponse<IWorkflowHygieneResponse>>(
      `/portfolio/workflow-hygiene${buildQuery(params)}`,
    );
    return result.data;
  },

  // Workflow Hygiene drill-down (PRODOPSAI-385). Each call hits an endpoint
  // that returns offender rows only — see the IUnlinkedItem etc. types for
  // shape. Pagination is server-side; pass `page` and `pageSize` via params.
  getUnlinkedItemsDrill: async (params: IPortfolioQueryParams & { page?: number; pageSize?: number }): Promise<IUnlinkedItemsDrillResponse> => {
    const result = await api.get<IApiResponse<IUnlinkedItemsDrillResponse>>(
      `/portfolio/workflow-hygiene/drill/unlinked${buildQuery(params)}`,
    );
    return result.data;
  },

  getNonSpineStatusesDrill: async (params: IPortfolioQueryParams & { page?: number; pageSize?: number }): Promise<INonSpineStatusesDrillResponse> => {
    const result = await api.get<IApiResponse<INonSpineStatusesDrillResponse>>(
      `/portfolio/workflow-hygiene/drill/statuses${buildQuery(params)}`,
    );
    return result.data;
  },

  getNonCanonicalTypesDrill: async (params: IPortfolioQueryParams & { page?: number; pageSize?: number }): Promise<INonCanonicalTypesDrillResponse> => {
    const result = await api.get<IApiResponse<INonCanonicalTypesDrillResponse>>(
      `/portfolio/workflow-hygiene/drill/types${buildQuery(params)}`,
    );
    return result.data;
  },

  getEpicBreakdown: async (params: IPortfolioQueryParams): Promise<IEpicBreakdownResponse> => {
    const result = await api.get<IApiResponse<IEpicBreakdownResponse>>(
      `/portfolio/epic-breakdown${buildQuery(params)}`,
    );
    return result.data;
  },

  // PRODOPSAI-416 / US6 — canonical WIP trend. Replaces the per-issue-type
  // breakdown returned by `issueInProgressDailyApi.getTrend(...)` with the
  // canonical aggregate from `flowMetricsRepository.getWipTrend` (Story/Bug/Task
  // hardcoded, archived=false). Response is one `{ date, count }` per snapshot
  // — daily; weekly bucketing happens client-side.
  getWipTrend: async (params: IPortfolioQueryParams): Promise<IWipTrendPoint[]> => {
    const result = await api.get<IApiResponse<IWipTrendPoint[]>>(
      `/portfolio/wip-trend${buildQuery(params)}`,
    );
    return result.data;
  },

  getDoraTrend: async (params: IPortfolioQueryParams): Promise<IPortfolioDoraTrendResponse> => {
    const result = await api.get<IApiResponse<IPortfolioDoraTrendResponse>>(
      `/portfolio/dora-trend${buildQuery({ ...params, granularity: 'weekly' })}`,
    );
    return result.data;
  },

  getDevExHistory: async (params: DevExHistoryParams): Promise<IDevExHistoryResponse> => {
    const search = new URLSearchParams();
    if (params.region) search.set('region', params.region);
    if (params.productId) search.set('productId', params.productId);
    if (params.teamId) search.set('teamId', params.teamId);
    const qs = search.toString();
    const result = await api.get<IApiResponse<IDevExHistoryResponse>>(
      qs ? `/portfolio/devex-history?${qs}` : '/portfolio/devex-history',
    );
    return result.data;
  },

  // PRODOPSAI-415: canonical WIP / Open wrappers. The backend repository
  // hardcodes the canonical scope (archived = false, issue_type ∈
  // {Story, Bug, Task}) and computes `asOfDate` internally — callers only
  // pass the optional scope filters.
  getCurrentWipCount: async (params: FlowMetricsQueryParams = {}): Promise<ICurrentWipCountResponse> => {
    const qs = buildFlowMetricsQuery(params);
    const result = await api.get<IApiResponse<ICurrentWipCountResponse>>(
      `/portfolio/current-wip-count${qs}`,
    );
    return result.data;
  },

  getCurrentWipAvgAge: async (params: FlowMetricsQueryParams = {}): Promise<IWipAvgAge> => {
    const qs = buildFlowMetricsQuery(params);
    const result = await api.get<IApiResponse<IWipAvgAge>>(
      `/portfolio/current-wip-avg-age${qs}`,
    );
    return result.data;
  },
};

// PRODOPSAI-415: separate query builder so we don't smuggle `from`/`to` as
// empty strings (the canonical WIP endpoints treat presence as "use this
// value"). Returns `""` when no filter is set so the URL stays clean.
function buildFlowMetricsQuery(params: FlowMetricsQueryParams): string {
  const search = new URLSearchParams();
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.region) search.set('region', params.region);
  if (params.productId) search.set('productId', params.productId);
  if (params.teamId) search.set('teamId', params.teamId);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}
